// src/app/shared/store/catalog/catalog.store.ts
// -----------------------------------------------------------------------------
// CatalogStore (NgRx SignalStore)
// -----------------------------------------------------------------------------
// ✅ UUID-first (no numeric ids)
// ✅ API-backed folders + items
// ✅ Tree computed for CatalogTree UI
// ✅ Selection UX (ported from mock store)
// ✅ Real mutations (create/rename/delete) wired to CatalogApiService
// ✅ setCurrentFolder alias kept for compatibility
// ✅ No "stub" methods that break TS signatures
// -----------------------------------------------------------------------------

import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import {
  getCatalogFolderDisplayName,
  getCatalogFolderPathDisplayName,
  normalizeFoldersPayload,
  folderDtoToVm,
  groupToCardVm,
  isInFolder,
} from './catalog.adapters';
import {
  CatalogCardVm,
  CatalogFolderCreateRequestDto,
  CatalogFolderDto,
  CatalogFolderPathNodeDto,
  CatalogFolderVm,
  CatalogItemAppliedFiltersDto,
  CatalogItemGroupDto,
  CatalogItemViewQuery,
  CatalogOrderEntryType,
  CatalogOrderMoveRequestDto,
  CatalogItemsQuery,
  CatalogItemsResponseDto,
  CatalogNormalizedItemType,
  Uuid,
} from './catalog.api.types';
import { CatalogApiService } from './catalog.api.service';
import { CatalogRouteState, CatalogViewMode } from './catalog-route-state';
import { getActiveAppLanguage, getActiveAppLocale } from '../../i18n/app-language.util';


/**
 * Tree node for UI tree (folders only).
 * Keep it small: branch component usually needs id/name/children.
 */
export interface CatalogTreeNode {
  id: Uuid;
  name: string;
  parent_id: Uuid | null;
  sort?: number | null;
  depth?: number;
  has_children?: boolean;
  path?: CatalogFolderPathNodeDto[];
  children: CatalogTreeNode[];
}

export interface CatalogState {
  // navigation
  catalogMode: CatalogViewMode;
  currentFolderId: Uuid | null;
  query: string;
  searchInCurrentFolder: boolean;

  // selection UX
  selectedIds: Set<Uuid>;
  treeSelectedId: Uuid | null;

  // data (RAW DTOs)
  folders: CatalogFolderDto[];                 // ✅ raw list from API
  foldersById: Record<Uuid, CatalogFolderDto>; // ✅ raw map
  itemsResp: CatalogItemsResponseDto | null;

  // loading
  foldersLoaded: boolean;
  isLoading: boolean;
  error: string | null;

  // paging / filters
  page: number;
  perPage: number;
  warehouseId: Uuid | null;
  accountId: Uuid | null;
  itemType: CatalogNormalizedItemType | null;
  catalogItemTypeId: Uuid | null;
}

export type CatalogSelectionKind = 'none' | 'folders' | 'items' | 'mixed';
export interface CatalogFolderFormValue {
  name: string;
  name_zh?: string | null;
  name_en?: string | null;
}

export type CatalogEntryVm =
  | {
      type: 'folder';
      id: Uuid;
      parentId: Uuid | null;
      displayOrder: number;
      name: string;
      folder: CatalogFolderVm;
    }
  | {
      type: 'model';
      id: Uuid;
      parentId: Uuid | null;
      displayOrder: number;
      name: string;
      product: CatalogCardVm;
    };

function filterSelectedFolderIds(
  selectedIds: ReadonlySet<Uuid>,
  foldersById: Record<Uuid, CatalogFolderDto>,
): Uuid[] {
  return Array.from(selectedIds).filter((id) => Boolean(foldersById[id]));
}

function filterSelectedProductIds(
  selectedIds: ReadonlySet<Uuid>,
  productIds: ReadonlySet<Uuid>,
): Uuid[] {
  return Array.from(selectedIds).filter((id) => productIds.has(id));
}

function isFolderDescendant(
  foldersById: Record<Uuid, CatalogFolderDto>,
  folderId: Uuid,
  targetParentId: Uuid | null,
): boolean {
  let cursor = targetParentId;
  const seen = new Set<Uuid>();

  while (cursor) {
    if (cursor === folderId) return true;
    if (seen.has(cursor)) break;

    seen.add(cursor);
    cursor = foldersById[cursor]?.parent_id ?? null;
  }

  return false;
}

function getCatalogGroupDisplayOrder(group: Pick<CatalogItemGroupDto, 'display_order'>): number {
  return Number(group.display_order ?? 0);
}

function compareCatalogOrder(
  left: { displayOrder: number; name: string; type: CatalogOrderEntryType; id: Uuid },
  right: { displayOrder: number; name: string; type: CatalogOrderEntryType; id: Uuid },
  locale: string,
): number {
  const byOrder = left.displayOrder - right.displayOrder;
  if (byOrder !== 0) return byOrder;

  const byName = left.name.localeCompare(right.name, locale, {
    sensitivity: 'base',
    numeric: true,
  });
  if (byName !== 0) return byName;

  const byType = left.type.localeCompare(right.type, 'en');
  if (byType !== 0) return byType;

  return left.id.localeCompare(right.id);
}

function mergeCatalogItemsResponse(
  current: CatalogItemsResponseDto | null,
  next: CatalogItemsResponseDto,
): CatalogItemsResponseDto {
  if (!current) return next;

  const mergedData = [...current.data];
  const indexById = new Map<Uuid, number>(mergedData.map((item, index) => [item.model_id, index] as const));

  for (const item of next.data) {
    const existingIndex = indexById.get(item.model_id);
    if (existingIndex === undefined) {
      indexById.set(item.model_id, mergedData.length);
      mergedData.push(item);
      continue;
    }

    mergedData[existingIndex] = item;
  }

  return {
    data: mergedData,
    meta: {
      ...next.meta,
      returned: mergedData.length,
      total: next.meta.total ?? current.meta.total,
    },
    refs: {
      warehouses: {
        ...current.refs.warehouses,
        ...next.refs.warehouses,
      },
      accounts: {
        ...current.refs.accounts,
        ...next.refs.accounts,
      },
      item_types: {
        ...current.refs.item_types,
        ...next.refs.item_types,
      },
      units: {
        ...current.refs.units,
        ...next.refs.units,
      },
      attr_options: {
        construction_type: {
          ...current.refs.attr_options.construction_type,
          ...next.refs.attr_options.construction_type,
        },
        run_in_state: {
          ...current.refs.attr_options.run_in_state,
          ...next.refs.attr_options.run_in_state,
        },
      },
    },
  };
}

export const CatalogStore = signalStore(
  { providedIn: 'root' },

  withState<CatalogState>({
    catalogMode: 'root',
    currentFolderId: null,
    query: '',
    searchInCurrentFolder: false,

    selectedIds: new Set<Uuid>(),
    treeSelectedId: null,

    folders: [],
    foldersById: {},
    itemsResp: null,

    foldersLoaded: false,
    isLoading: false,
    error: null,

    page: 1,
    perPage: 30,
    warehouseId: null,
    accountId: null,
    itemType: null,
    catalogItemTypeId: null,
  }),

  withComputed((store) => {
    const currentFolder = computed(() => {
      if (store.catalogMode() !== 'folder') return null;
      const id = store.currentFolderId();
      return id ? store.foldersById()[id] ?? null : null;
    });

    /**
     * ✅ RENAMED: folderCards (VM list for UI)
     * Avoid collision with state member "folders".
     */
    const folderCards = computed<CatalogFolderVm[]>(() => {
      const language = getActiveAppLanguage();
      const locale = getActiveAppLocale();
      const mode = store.catalogMode();
      if (mode === 'all') return [];

      const pid = mode === 'folder' ? store.currentFolderId() : null;
      return store
        .folders()
        .filter((f) => (f.parent_id ?? null) === pid)
        .slice()
        .sort(
          (a, b) =>
            (a.sort ?? 0) - (b.sort ?? 0) ||
            getCatalogFolderDisplayName(a, language).localeCompare(
              getCatalogFolderDisplayName(b, language),
              locale,
              { sensitivity: 'base' },
            ),
        )
        .map(folderDtoToVm);
    });

    const visibleGroups = computed<CatalogItemGroupDto[]>(() => {
      const resp = store.itemsResp();
      const groups = resp?.data ?? [];
      const mode = store.catalogMode();
      const folderId = mode === 'folder' ? store.currentFolderId() : null;

      return mode === 'all' ? groups : groups.filter((g) => isInFolder(g, folderId));
    });

    const orderedVisibleGroups = computed<CatalogItemGroupDto[]>(() => {
      const locale = getActiveAppLocale();
      return visibleGroups()
        .slice()
        .sort((a, b) =>
          compareCatalogOrder(
            {
              type: 'model',
              id: a.model_id,
              displayOrder: getCatalogGroupDisplayOrder(a),
              name: a.name ?? '',
            },
            {
              type: 'model',
              id: b.model_id,
              displayOrder: getCatalogGroupDisplayOrder(b),
              name: b.name ?? '',
            },
            locale,
          ),
        );
    });

    const products = computed<CatalogCardVm[]>(() => {
      return orderedVisibleGroups()
        .map(groupToCardVm);
    });

    const catalogEntries = computed<CatalogEntryVm[]>(() => {
      const language = getActiveAppLanguage();
      const locale = getActiveAppLocale();
      const mode = store.catalogMode();
      const includeFolders = mode !== 'all' && !store.query().trim();
      const parentId = mode === 'folder' ? store.currentFolderId() : null;
      const entries: CatalogEntryVm[] = [];

      if (includeFolders) {
        for (const folder of store.folders().filter((f) => (f.parent_id ?? null) === parentId)) {
          const folderVm = folderDtoToVm(folder);
          const name = getCatalogFolderDisplayName(folder, language);
          entries.push({
            type: 'folder',
            id: folder.id,
            parentId: folder.parent_id ?? null,
            displayOrder: folder.sort ?? 0,
            name,
            folder: {
              ...folderVm,
              name,
            },
          });
        }
      }

      for (const group of orderedVisibleGroups()) {
        const product = groupToCardVm(group);
        entries.push({
          type: 'model',
          id: group.model_id,
          parentId: group.folder_id ?? null,
          displayOrder: getCatalogGroupDisplayOrder(group),
          name: product.name,
          product,
        });
      }

      return entries
        .slice()
        .sort((a, b) => compareCatalogOrder(a, b, locale));
    });

    const tree = computed<CatalogTreeNode[]>(() => {
      const language = getActiveAppLanguage();
      const locale = getActiveAppLocale();
      const src = store.folders();

      const byParent = new Map<string, CatalogFolderDto[]>();
      for (const f of src) {
        const pid = (f.parent_id ?? '__root__') as string;
        const arr = byParent.get(pid);
        if (arr) arr.push(f);
        else byParent.set(pid, [f]);
      }

      const build = (parentId: Uuid | null, depth = 0): CatalogTreeNode[] => {
        const key = (parentId ?? '__root__') as string;
        const children = (byParent.get(key) ?? []).slice();

        children.sort(
          (a, b) =>
            (a.sort ?? 0) - (b.sort ?? 0) ||
            getCatalogFolderDisplayName(a, language).localeCompare(
              getCatalogFolderDisplayName(b, language),
              locale,
              { sensitivity: 'base' },
            ),
        );

        return children.map((f) => ({
          id: f.id,
          name: getCatalogFolderDisplayName(f, language),
          parent_id: (f.parent_id ?? null) as Uuid | null,
          sort: f.sort ?? null,
          depth: f.depth ?? depth,
          has_children: f.has_children ?? (byParent.get(f.id)?.length ?? 0) > 0,
          path: f.path,
          children: build(f.id, depth + 1),
        }));
      };

      return build(null);
    });

    const selectedFolderIds = computed(() =>
      filterSelectedFolderIds(store.selectedIds(), store.foldersById()),
    );

    const selectedFolderSet = computed(() => new Set(selectedFolderIds()));
    const visibleProductIds = computed(() => products().map((item) => item.id));
    const visibleProductSet = computed(() => new Set(visibleProductIds()));
    const selectedProductIds = computed(() =>
      filterSelectedProductIds(store.selectedIds(), visibleProductSet()),
    );
    const selectedProductSet = computed(() => new Set(selectedProductIds()));

    // UX computed (ported)
    const isLoaded = computed(() => !store.isLoading());
    const selectedCount = computed(() => selectedFolderIds().length + selectedProductIds().length);
    const isSelectionMode = computed(() => selectedCount() > 0);
    const selectedEntityKind = computed<CatalogSelectionKind>(() => {
      const hasFolders = selectedFolderIds().length > 0;
      const hasItems = selectedProductIds().length > 0;

      if (hasFolders && hasItems) return 'mixed';
      if (hasFolders) return 'folders';
      if (hasItems) return 'items';
      return 'none';
    });
    const isSelected = computed(
      () => (id: Uuid) => selectedFolderSet().has(id) || selectedProductSet().has(id),
    );
    const isProductSelectable = computed(() => (id: Uuid) => visibleProductSet().has(id));

    const allSelected = computed(() => {
      const total = folderCards().length + products().length;
      return total > 0 && selectedCount() === total;
    });

    const currentFolderName = computed(() => {
      const folder = currentFolder();
      if (folder?.path?.length) return getCatalogFolderPathDisplayName(folder.path);
      if (folder) return getCatalogFolderDisplayName(folder);
      return store.catalogMode() === 'all' ? 'Каталог' : 'Корень';
    });
    const appliedFilters = computed<CatalogItemAppliedFiltersDto | null>(
      () => store.itemsResp()?.meta.applied_filters ?? null,
    );
    const scopeQuery = computed<CatalogItemViewQuery>(() => {
      const filters = appliedFilters();
      return {
        warehouse_id: filters?.warehouse_id ?? store.warehouseId() ?? undefined,
        account_id: filters?.account_id ?? store.accountId() ?? undefined,
        item_type: filters?.item_type ?? store.itemType() ?? undefined,
        catalog_item_type_id:
          filters?.catalog_item_type_id ?? store.catalogItemTypeId() ?? undefined,
      };
    });

    const selectedItemName = computed(() => {
      const selected = selectedFolderIds();
      if (selected.length !== 1) return '';

      const id = selected[0];

      const f = store.foldersById()[id];
      if (f?.path?.length) return getCatalogFolderPathDisplayName(f.path);
      if (f) return getCatalogFolderDisplayName(f);
      return '';
    });

    const isTreeSelected = computed(() => (id: Uuid) => store.treeSelectedId() === id);
    const hasMore = computed(() => Boolean(store.itemsResp()?.meta.has_more));
    const loadedProductCount = computed(() => store.itemsResp()?.data.length ?? 0);

    return {
      currentFolder,

      // ✅ expose renamed VM list
      folderCards,
      products,
      catalogEntries,
      tree,

      // UX
      isLoaded,
      isSelectionMode,
      selectedCount,
      selectedFolderIds,
      selectedProductIds,
      selectedEntityKind,
      isSelected,
      isProductSelectable,
      allSelected,
      currentFolderName,
      appliedFilters,
      scopeQuery,
      selectedItemName,
      isTreeSelected,
      hasMore,
      loadedProductCount,

      // raw accessors
      foldersRaw: computed(() => store.folders()),
      itemsRaw: computed(() => store.itemsResp()),
      refs: computed(() => store.itemsResp()?.refs ?? null),
    };
  }),

  // ---------------------------------------------------------------------------
  // Methods
  // ---------------------------------------------------------------------------
  withMethods((store) => {
    const api = inject(CatalogApiService);
    let itemsRequestId = 0;
    let foldersRequest: Promise<void> | null = null;

    // -------------------------------- query builders --------------------------------

    function buildItemsQuery(): CatalogItemsQuery {
      const mode = store.catalogMode();
      const folderId = mode === 'folder' ? store.currentFolderId() : null;
      const search = (store.query() || '').trim();
      const searchInCurrentFolder = store.searchInCurrentFolder();
      const query: CatalogItemsQuery = {
        q: search || undefined,
        warehouse_id: store.warehouseId() ?? undefined,
        account_id: store.accountId() ?? undefined,
        item_type: store.itemType() ?? undefined,
        catalog_item_type_id: store.catalogItemTypeId() ?? undefined,
        page: store.page(),
        per_page: store.perPage(),
      };

      if (search && !searchInCurrentFolder) {
        return query;
      }

      if (mode === 'root') {
        query.folder_id = '';
      } else if (mode === 'folder' && folderId) {
        query.folder_id = folderId;
      }

      return query;
    }

    // -------------------------------- loaders --------------------------------

    async function loadFolders(): Promise<void> {
      const raw = await firstValueFrom(api.listFolders());

      // Prefer backend-provided flat map (fast, already normalized).
      // Fallback to your current normalizer if server returns only tree/items.
      const flat = raw?.flat;
      if (flat && typeof flat === 'object') {
        const list: CatalogFolderDto[] = Object.values(flat).map((n) => ({
          id: n.id,
          name: n.name,
          name_zh: n.name_zh ?? null,
          name_en: n.name_en ?? null,
          parent_id: n.parent_id ?? null,
          sort: n.sort ?? 0,
          depth: n.depth,
          has_children: n.has_children,
          path: n.path ?? [],
          created_at: n.created_at,
          updated_at: n.updated_at,
        }));

        const byId: Record<Uuid, CatalogFolderDto> = {};
        for (const f of list) byId[f.id] = f;

        patchState(store, { folders: list, foldersById: byId, foldersLoaded: true });
        return;
      }

      // Legacy / mixed payload support (your existing adapter)
      const list = normalizeFoldersPayload(raw);
      const byId: Record<Uuid, CatalogFolderDto> = {};
      for (const f of list) byId[f.id] = f;

      patchState(store, { folders: list, foldersById: byId, foldersLoaded: true });
    }

    async function loadItems(requestId: number, append = false): Promise<void> {
      const resp = await firstValueFrom(api.listItems(buildItemsQuery()));
      if (requestId !== itemsRequestId) return;
      patchState(store, {
        itemsResp: append ? mergeCatalogItemsResponse(store.itemsResp(), resp) : resp,
      });
    }

    async function reloadItemsState(errorMessage: string): Promise<void> {
      const requestId = ++itemsRequestId;

      patchState(store, { isLoading: true, error: null });
      try {
        await loadItems(requestId);
      } catch {
        if (requestId === itemsRequestId) {
          patchState(store, { error: errorMessage });
        }
      } finally {
        if (requestId === itemsRequestId) {
          patchState(store, { isLoading: false });
        }
      }
    }

    async function ensureFoldersLoaded(force = false): Promise<void> {
      if (!force && store.foldersLoaded()) return;
      if (foldersRequest) return foldersRequest;

      foldersRequest = (async () => {
        await loadFolders();
      })();

      try {
        await foldersRequest;
      } finally {
        foldersRequest = null;
      }
    }

    // -------------------------------- public API --------------------------------

    async function init(): Promise<void> {
      const requestId = ++itemsRequestId;

      patchState(store, { isLoading: true, error: null });
      try {
        await ensureFoldersLoaded();
        await loadItems(requestId);
      } catch (e) {
        if (requestId === itemsRequestId) {
          patchState(store, { error: 'Failed to load catalog' });
        }
      } finally {
        if (requestId === itemsRequestId) {
          patchState(store, { isLoading: false });
        }
      }
    }

    async function applyRouteState(routeState: CatalogRouteState): Promise<void> {
      const mode: CatalogViewMode =
        routeState.mode === 'folder' && routeState.folderId ? 'folder' : routeState.mode === 'all' ? 'all' : 'root';
      const folderId = mode === 'folder' ? routeState.folderId : null;
      const warehouseId = routeState.warehouse_id ?? null;
      const accountId = routeState.account_id ?? null;
      const itemType = routeState.item_type ?? null;
      const catalogItemTypeId = routeState.catalog_item_type_id ?? null;

      const routeChanged =
        store.catalogMode() !== mode ||
        store.currentFolderId() !== folderId ||
        store.warehouseId() !== warehouseId ||
        store.accountId() !== accountId ||
        store.itemType() !== itemType ||
        store.catalogItemTypeId() !== catalogItemTypeId;

      if (routeChanged) {
        patchState(store, {
          catalogMode: mode,
          currentFolderId: folderId,
          warehouseId,
          accountId,
          itemType,
          catalogItemTypeId,
          selectedIds: new Set<Uuid>(),
          treeSelectedId: folderId,
          page: 1,
        });
      }

      await ensureFoldersLoaded();

      if (routeChanged || !store.itemsResp()) {
        await reloadItemsState('Failed to load items');
      }
    }

    async function setFolder(id: Uuid | null): Promise<void> {
      // reset selection as in old UX
      patchState(store, {
        catalogMode: id ? 'folder' : 'root',
        currentFolderId: id,
        selectedIds: new Set<Uuid>(),
        treeSelectedId: id,
        page: 1,
      });

      await reloadItemsState('Failed to load items');
    }

    /**
     * Compatibility alias — legacy code calls setCurrentFolder(...)
     * Keep until you refactor all callers.
     */
    const setCurrentFolder = setFolder;

    async function setQuery(q: string): Promise<void> {
      patchState(store, { query: q ?? '', page: 1 });
      await reloadItemsState('Failed to search items');
    }

    async function setSearchInCurrentFolder(value: boolean): Promise<void> {
      const normalized = Boolean(value);
      if (store.searchInCurrentFolder() === normalized) return;

      patchState(store, {
        searchInCurrentFolder: normalized,
        page: 1,
      });
      await reloadItemsState('Failed to search items');
    }

    // -------------------------------- selection --------------------------------

    function selectTreeFolder(id: Uuid | null) {
      patchState(store, { treeSelectedId: id });
    }

    function toggleSelection(id: Uuid) {
      const isFolder = Boolean(store.foldersById()[id]);
      const isProduct = store.isProductSelectable()(id);
      if (!isFolder && !isProduct) return;

      const set = new Set(store.selectedIds());
      if (set.has(id)) {
        set.delete(id);
        patchState(store, { selectedIds: set });
        return;
      }

      const currentKind = store.selectedEntityKind();
      const shouldResetSelection =
        (isFolder && (currentKind === 'items' || currentKind === 'mixed')) ||
        (isProduct && (currentKind === 'folders' || currentKind === 'mixed'));

      if (shouldResetSelection) {
        set.clear();
      }

      set.add(id);
      patchState(store, { selectedIds: set });
    }

    function clearSelection() {
      patchState(store, { selectedIds: new Set<Uuid>(), treeSelectedId: null });
    }

    function selectAllVisible() {
      const folderIds = store.folderCards().map((folder) => folder.id);
      const productIds = store.products().map((item) => item.id);
      const kind = store.selectedEntityKind();

      if (kind === 'folders' && folderIds.length > 0) {
        patchState(store, { selectedIds: new Set<Uuid>(folderIds) });
        return;
      }

      if (productIds.length > 0) {
        patchState(store, { selectedIds: new Set<Uuid>(productIds) });
        return;
      }

      patchState(store, { selectedIds: new Set<Uuid>(folderIds) });
    }

    // -------------------------------- mutations --------------------------------

    async function createFolder(folderData: CatalogFolderFormValue): Promise<CatalogFolderDto | null> {
      const parentId = store.catalogMode() === 'folder' ? store.currentFolderId() : null;
      const body: CatalogFolderCreateRequestDto = {
        name: folderData.name.trim(),
        name_zh: folderData.name_zh?.trim() || null,
        name_en: folderData.name_en?.trim() || null,
        parent_id: parentId,
        sort: 0,
      };

      patchState(store, { isLoading: true, error: null });
      try {
        const created = await firstValueFrom(api.createFolder(body));

        // refresh folder cache + tree; keep items as-is (no need to reload)
        await loadFolders();

        return created;
      } catch {
        patchState(store, { error: 'Failed to create folder' });
        return null;
      } finally {
        patchState(store, { isLoading: false });
      }
    }

    async function renameSelected(folderData: CatalogFolderFormValue): Promise<void> {
      const selected = filterSelectedFolderIds(store.selectedIds(), store.foldersById());
      if (selected.length !== 1) return;

      const id = selected[0];
      const folder = store.foldersById()[id];

      // Only folders can be renamed currently
      if (!folder) return;

      patchState(store, { isLoading: true, error: null });
      try {
        await firstValueFrom(api.patchFolder(id, {
          name: folderData.name.trim(),
          name_zh: folderData.name_zh?.trim() || null,
          name_en: folderData.name_en?.trim() || null,
        }));
        await loadFolders();
        patchState(store, { selectedIds: new Set<Uuid>() });
      } catch {
        patchState(store, { error: 'Failed to rename folder' });
      } finally {
        patchState(store, { isLoading: false });
      }
    }

    async function deleteSelected(): Promise<void> {
      const selected = filterSelectedFolderIds(store.selectedIds(), store.foldersById());
      if (selected.length !== 1) return;

      const id = selected[0];
      const folder = store.foldersById()[id];
      if (!folder) return;

      patchState(store, { isLoading: true, error: null });
      try {
        await firstValueFrom(api.deleteFolder(id));
        const requestId = ++itemsRequestId;
        await loadFolders();
        await loadItems(requestId);
        patchState(store, { selectedIds: new Set<Uuid>() });
      } catch {
        patchState(store, { error: 'Failed to delete folder' });
      } finally {
        patchState(store, { isLoading: false });
      }
    }

    async function moveSelectedToFolder(targetFolderId: Uuid | null): Promise<boolean> {
      const selectedIds = filterSelectedFolderIds(store.selectedIds(), store.foldersById());
      if (selectedIds.length !== 1) return false;

      const folderId = selectedIds[0];
      const folder = store.foldersById()[folderId];
      if (!folder) return false;

      // Нельзя перемещать папку в саму себя или в собственную дочернюю ветку.
      if (targetFolderId === folderId || isFolderDescendant(store.foldersById(), folderId, targetFolderId)) {
        return false;
      }

      // Если папка уже находится в выбранном месте, сетевой вызов не нужен.
      if ((folder.parent_id ?? null) === targetFolderId) {
        patchState(store, { selectedIds: new Set<Uuid>(), treeSelectedId: null });
        return true;
      }

      patchState(store, { isLoading: true, error: null });
      try {
        await firstValueFrom(api.patchFolder(folderId, { parent_id: targetFolderId }));
        const requestId = ++itemsRequestId;
        await loadFolders();
        await loadItems(requestId);
        patchState(store, {
          selectedIds: new Set<Uuid>(),
          treeSelectedId: null,
        });
        return true;
      } catch {
        patchState(store, { error: 'Failed to move folder' });
        return false;
      } finally {
        patchState(store, { isLoading: false });
      }
    }

    async function moveSelectedItemsToFolder(targetFolderId: Uuid | null): Promise<boolean> {
      const selectedIds = store.selectedProductIds();
      if (!selectedIds.length) return false;

      patchState(store, { isLoading: true, error: null });
      try {
        await firstValueFrom(
          api.moveItems({
            item_ids: selectedIds,
            folder_id: targetFolderId,
          }),
        );
        const requestId = ++itemsRequestId;
        await loadItems(requestId);
        patchState(store, {
          selectedIds: new Set<Uuid>(),
          treeSelectedId: null,
        });
        return true;
      } catch {
        patchState(store, { error: 'Failed to move items' });
        return false;
      } finally {
        patchState(store, { isLoading: false });
      }
    }

    async function moveCatalogEntryOrder(body: CatalogOrderMoveRequestDto): Promise<boolean> {
      patchState(store, { isLoading: true, error: null });
      try {
        await firstValueFrom(api.moveOrder(body));
        const requestId = ++itemsRequestId;
        await loadFolders();
        await loadItems(requestId);
        return true;
      } catch {
        const requestId = ++itemsRequestId;
        try {
          await loadFolders();
          await loadItems(requestId);
        } catch {
          // Если refresh после ошибки тоже упал, сохраняем исходную ошибку reorder.
        }
        patchState(store, { error: 'Failed to reorder catalog' });
        return false;
      } finally {
        patchState(store, { isLoading: false });
      }
    }

    // -------------------------------- optional helpers --------------------------------

    async function setWarehouse(id: Uuid | null): Promise<void> {
      patchState(store, { warehouseId: id, page: 1 });
      await reloadItemsState('Failed to load items');
    }

    async function setPage(page: number): Promise<void> {
      patchState(store, { page: Math.max(1, page | 0) });
      await reloadItemsState('Failed to load items');
    }

    async function setPerPage(perPage: number): Promise<void> {
      patchState(store, { perPage: Math.max(1, perPage | 0), page: 1 });
      await reloadItemsState('Failed to load items');
    }

    async function loadNextPage(): Promise<void> {
      if (store.isLoading()) return;
      if (!store.hasMore()) return;

      const previousPage = store.page();
      const nextPage = previousPage + 1;
      const requestId = ++itemsRequestId;

      patchState(store, {
        page: nextPage,
        isLoading: true,
        error: null,
      });

      try {
        await loadItems(requestId, true);
      } catch {
        if (requestId === itemsRequestId) {
          patchState(store, {
            page: previousPage,
            error: 'Failed to load more items',
          });
        }
      } finally {
        if (requestId === itemsRequestId) {
          patchState(store, { isLoading: false });
        }
      }
    }

    return {
      // bootstrap
      init,
      ensureFoldersLoaded,
      applyRouteState,

      // navigation
      setFolder,
      setCurrentFolder, // compat
      setQuery,
      setSearchInCurrentFolder,

      // selection
      selectTreeFolder,
      toggleSelection,
      clearSelection,
      selectAllVisible,

      // mutations
      createFolder,
      renameSelected,
      deleteSelected,
      moveSelectedToFolder,
      moveSelectedItemsToFolder,
      moveCatalogEntryOrder,

      // optional filters/paging
      setWarehouse,
      async setAccount(id: Uuid | null): Promise<void> {
        patchState(store, { accountId: id, page: 1 });
        await reloadItemsState('Failed to load items');
      },
      async setItemType(itemType: CatalogNormalizedItemType | null): Promise<void> {
        patchState(store, { itemType, page: 1 });
        await reloadItemsState('Failed to load items');
      },
      async setCatalogItemType(id: Uuid | null): Promise<void> {
        patchState(store, { catalogItemTypeId: id, page: 1 });
        await reloadItemsState('Failed to load items');
      },
      setPage,
      setPerPage,
      loadNextPage,
    };
  }),
);
